import SettingsIcon from '@mui/icons-material/Settings'
import { Button, CircularProgress, Divider, IconButton, Popover, Stack, Tooltip, Typography } from '@mui/material'
import React, { useState } from 'react'
import AutoReloadSelectField from '../../components/autoReload/AutoReloadSelectField'
import { ACTIVITY_AUTO_RELOAD_INTERVALS, useAutoReloadControl } from '../../components/autoReload/useAutoReloadControl'
import { useAppTranslations } from '../../utils/hooks/useAppTranslations'
import { useUserPermissions } from '../company/userPermissions'

type ActivityFeedSettingsMenuProps = {
  autoReloadDisabledReason?: string
  autoReloadEnabled?: boolean
  isRefetching?: boolean
  onAutoReload: () => void | Promise<void>
  onShowAllChangesChange: (showAllChanges: boolean) => void
  showAllChanges: boolean
}

const ActivityFeedSettingsMenu: React.FC<ActivityFeedSettingsMenuProps> = ({
  autoReloadDisabledReason,
  autoReloadEnabled = true,
  isRefetching = false,
  onAutoReload,
  onShowAllChangesChange,
  showAllChanges,
}) => {
  const t = useAppTranslations()
  const { superAdmin } = useUserPermissions()
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const autoReloadControl = useAutoReloadControl({
    defaultInterval: '1m',
    enabled: superAdmin && autoReloadEnabled,
    intervals: ACTIVITY_AUTO_RELOAD_INTERVALS,
    onReload: onAutoReload,
    storageKey: 'activity.autoReload',
    translationNamespace: 'activity',
  })

  return (
    <>
      <Tooltip title={t('commons.settingsTitle')}>
        <IconButton onClick={(event) => setAnchorEl(event.currentTarget)} size="small">
          {isRefetching ? <CircularProgress size={16} /> : <SettingsIcon />}
        </IconButton>
      </Tooltip>
      <Popover
        anchorEl={anchorEl}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        onClose={() => setAnchorEl(null)}
        open={Boolean(anchorEl)}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
      >
        <Stack sx={{ gap: 1.5, minWidth: 220, p: 2 }}>
          <Button
            fullWidth
            onClick={() => onShowAllChangesChange(!showAllChanges)}
            variant={showAllChanges ? 'contained' : 'outlined'}
          >
            {showAllChanges ? t('activity.feed.showLimitedChanges') : t('activity.feed.showAllChanges')}
          </Button>
          {superAdmin ? (
            <>
              <Divider />
              {!autoReloadEnabled && autoReloadDisabledReason ? (
                <Typography color="text.secondary" variant="caption">
                  {autoReloadDisabledReason}
                </Typography>
              ) : null}
              <AutoReloadSelectField
                getIntervalLabel={autoReloadControl.getIntervalLabel}
                intervalKeys={autoReloadControl.intervalKeys}
                onIntervalChange={autoReloadControl.onIntervalChange}
                selectedInterval={autoReloadControl.selectedInterval}
                storageKey={autoReloadControl.storageKey}
              />
            </>
          ) : null}
        </Stack>
      </Popover>
    </>
  )
}

export default ActivityFeedSettingsMenu
